"use strict";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAccountMeta } from "contexts/UniswapContext";
import { useEnabledChains } from "features/chains/hooks/useEnabledChains";
import { UniverseChainId } from "features/chains/types";
import { ValueType, getCurrencyAmount } from "features/tokens/getCurrencyAmount";
import { useCurrencyInfo } from "features/tokens/useCurrencyInfo";
import { getBscContracts, isBscChain, MULTICALL3_ADDRESS } from "constants/bscContracts";
import { fetchPoolsLazy } from "features/routing/api/bscSubgraph";
import { useQuery } from "@tanstack/react-query";
import { buildCurrencyId } from "utils/uniswapCurrencyId";
import { Contract } from "ethers";
import { useEthersProvider } from "hooks/useEthersProvider";
import { useTransactionSettingsContext } from "features/transactions/settings/contexts/TransactionSettingsContext";
import { getCommonIntermediaries } from "features/routing/constants/intermediaries";
import {
  getV2Quote,
  getV3Quote,
  compareTrades,
  createTradeObject
} from "features/routing/utils/bscQuoteUtils";
import { routingError } from "features/routing/utils/debug";
const QUOTER_V2_ABI = [
  // Multihop + single-hop via bytes path (Slipstream)
  {
    name: "quoteExactInput",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "path", type: "bytes" },
      { name: "amountIn", type: "uint256" }
    ],
    outputs: [
      { name: "amountOut", type: "uint256" },
      { name: "sqrtPriceX96AfterList", type: "uint160[]" },
      { name: "initializedTicksCrossedList", type: "uint32[]" },
      { name: "gasEstimate", type: "uint256" }
    ]
  },
  {
    name: "quoteExactOutput",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "path", type: "bytes" },
      { name: "amountOut", type: "uint256" }
    ],
    outputs: [
      { name: "amountIn", type: "uint256" },
      { name: "sqrtPriceX96AfterList", type: "uint160[]" },
      { name: "initializedTicksCrossedList", type: "uint32[]" },
      { name: "gasEstimate", type: "uint256" }
    ]
  },
  // Keep single-hop structs for potential legacy usage (not used for Slipstream multihop)
  {
    name: "quoteExactInputSingle",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "params", type: "tuple", components: [
      { name: "tokenIn", type: "address" },
      { name: "tokenOut", type: "address" },
      { name: "amountIn", type: "uint256" },
      { name: "tickSpacing", type: "int24" },
      { name: "sqrtPriceLimitX96", type: "uint160" }
    ] }],
    outputs: [
      { name: "amountOut", type: "uint256" },
      { name: "sqrtPriceX96After", type: "uint160" },
      { name: "initializedTicksCrossed", type: "uint32" },
      { name: "gasEstimate", type: "uint256" }
    ]
  },
  {
    name: "quoteExactOutputSingle",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "params", type: "tuple", components: [
      { name: "tokenIn", type: "address" },
      { name: "tokenOut", type: "address" },
      { name: "amountOut", type: "uint256" },
      { name: "tickSpacing", type: "int24" },
      { name: "sqrtPriceLimitX96", type: "uint160" }
    ] }],
    outputs: [
      { name: "amountIn", type: "uint256" },
      { name: "sqrtPriceX96After", type: "uint160" },
      { name: "initializedTicksCrossed", type: "uint32" },
      { name: "gasEstimate", type: "uint256" }
    ]
  }
];
const MULTICALL3_ABI = [
  {
    inputs: [{ components: [{ name: "target", type: "address" }, { name: "callData", type: "bytes" }], name: "calls", type: "tuple[]" }],
    name: "aggregate",
    outputs: [{ name: "blockNumber", type: "uint256" }, { name: "returnData", type: "bytes[]" }],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [
      { name: "requireSuccess", type: "bool" },
      { components: [{ name: "target", type: "address" }, { name: "callData", type: "bytes" }], name: "calls", type: "tuple[]" }
    ],
    name: "tryAggregate",
    outputs: [{ components: [{ name: "success", type: "bool" }, { name: "returnData", type: "bytes" }], name: "results", type: "tuple[]" }],
    stateMutability: "payable",
    type: "function"
  }
];
const MIXED_ROUTE_QUOTER_ABI = [
  {
    name: "quoteExactInput",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "path", type: "bytes" },
      { name: "amountIn", type: "uint256" }
    ],
    outputs: [
      { name: "amountOut", type: "uint256" },
      { name: "v3SqrtPriceX96AfterList", type: "uint160[]" },
      { name: "v3InitializedTicksCrossedList", type: "uint32[]" },
      { name: "v3SwapGasEstimate", type: "uint256" }
    ]
  }
];
function useLazyPoolData(tokenIn, tokenOut, chainId = UniverseChainId.Mainnet, skip = false) {
  return useQuery({
    queryKey: ["pool-data-lazy", chainId, tokenIn?.toLowerCase(), tokenOut?.toLowerCase()],
    queryFn: () => {
      if (!tokenIn || !tokenOut) {
        return { v2Pools: [], v3Pools: [] };
      }
      const commonBases = getCommonIntermediaries(chainId);
      return fetchPoolsLazy(tokenIn, tokenOut, chainId, commonBases);
    },
    enabled: !skip && Boolean(tokenIn) && Boolean(tokenOut),
    staleTime: 12e4,
    // 2 minutes - pools don't change that frequently
    gcTime: 3e5,
    // 5 minutes - keep in cache longer
    refetchOnWindowFocus: false
    // Don't refetch on window focus
  });
}
export function useBscQuote({
  tokenIn,
  tokenOut,
  amountSpecified,
  tradeType,
  chainId,
  skip = false,
  skipV2 = false,
  skipV3 = false
}) {
  const [trade, setTrade] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [quoteUpdatedAt, setQuoteUpdatedAt] = useState(null);
  const refetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);
  const [_v2Loading, setV2Loading] = useState(false);
  const [_v3Loading, setV3Loading] = useState(false);
  const [_v2Loaded, setV2Loaded] = useState(false);
  const [_v3Loaded, setV3Loaded] = useState(false);
  const _account = useAccountMeta();
  const { defaultChainId } = useEnabledChains();
  const effectiveChainId = chainId ?? defaultChainId;
  const { customSlippageTolerance, autoSlippageTolerance, updateTransactionSettings } = useTransactionSettingsContext();
  const FOT_AUTO_SLIPPAGE_BY_ADDRESS = {
    "0xf3c571aa528c03d64fa825af7bc544fd1953ffff": 4,
    // intCLAW (3% tax)
    "0xcc935be86c9a12837cc2c27b43fa2f8aa69affff": 4
    // xPBNB (3% tax)
  };
  const fotAutoSlippage = useMemo(() => {
    const inAddr = tokenIn?.toLowerCase() ?? "";
    const outAddr = tokenOut?.toLowerCase() ?? "";
    const inSlippage = FOT_AUTO_SLIPPAGE_BY_ADDRESS[inAddr];
    const outSlippage = FOT_AUTO_SLIPPAGE_BY_ADDRESS[outAddr];
    if (inSlippage === void 0 && outSlippage === void 0) return void 0;
    return Math.max(inSlippage ?? 0, outSlippage ?? 0);
  }, [tokenIn, tokenOut]);
  useEffect(() => {
    updateTransactionSettings({
      autoSlippageTolerance: fotAutoSlippage
    });
  }, [fotAutoSlippage, updateTransactionSettings]);
  const bscContracts = getBscContracts(effectiveChainId);
  const { data: poolData, isLoading: poolsLoading } = useLazyPoolData(
    tokenIn,
    tokenOut,
    effectiveChainId,
    skip
  );
  const currencyInInfo = useCurrencyInfo(
    tokenIn && effectiveChainId ? buildCurrencyId(effectiveChainId, tokenIn) : void 0
  );
  const currencyOutInfo = useCurrencyInfo(
    tokenOut && effectiveChainId ? buildCurrencyId(effectiveChainId, tokenOut) : void 0
  );
  const currencyIn = currencyInInfo?.currency;
  const currencyOut = currencyOutInfo?.currency;
  const shouldSkip = useMemo(() => {
    return skip || !tokenIn || !tokenOut || !amountSpecified || !currencyIn || !currencyOut || !isBscChain(effectiveChainId) || amountSpecified === "0" || amountSpecified === void 0 || tokenIn === tokenOut || poolsLoading || !poolData;
  }, [skip, tokenIn, tokenOut, amountSpecified, currencyIn, currencyOut, effectiveChainId, poolsLoading, poolData]);
  const inputAmount = useMemo(() => {
    if (shouldSkip || !currencyIn || !amountSpecified) return null;
    return getCurrencyAmount({
      value: amountSpecified,
      valueType: ValueType.Raw,
      currency: currencyIn
    });
  }, [shouldSkip, currencyIn, amountSpecified]);
  const ethersProvider = useEthersProvider({ chainId: effectiveChainId });
  const quoteRunIdRef = useRef(0);
  useEffect(() => {
    const runId = ++quoteRunIdRef.current;
    if (shouldSkip) {
      setTrade(null);
      setIsLoading(false);
      setError(null);
      setV2Loaded(false);
      setV3Loaded(false);
      setV2Loading(false);
      setV3Loading(false);
      setQuoteUpdatedAt(null);
      return;
    }
    setTrade(null);
    setQuoteUpdatedAt(null);
    async function findBestTrade() {
      try {
        setIsLoading(true);
        setError(null);
        setV2Loaded(false);
        setV3Loaded(false);
        setV2Loading(true);
        setV3Loading(true);
        const { v2Pools: allV2Pools, v3Pools: allV3Pools } = poolData;
        const v2Params = {
          currencyIn,
          currencyOut,
          inputAmount,
          amountSpecified,
          tradeType,
          poolData: allV2Pools,
          effectiveChainId,
          routerAddress: bscContracts.router,
          factoryAddress: bscContracts.poolFactory,
          ethersProvider,
          multicallContract: new Contract(MULTICALL3_ADDRESS, MULTICALL3_ABI, ethersProvider)
        };
        const v3Params = {
          tokenIn,
          tokenOut,
          currencyIn,
          currencyOut,
          inputAmount,
          amountSpecified,
          tradeType,
          v3Pools: allV3Pools,
          quoterContract: new Contract(bscContracts.quoterV2, QUOTER_V2_ABI, ethersProvider),
          multicallContract: new Contract(MULTICALL3_ADDRESS, MULTICALL3_ABI, ethersProvider),
          mixedQuoterContract: bscContracts?.mixedRouteQuoter ? new Contract(bscContracts.mixedRouteQuoter, MIXED_ROUTE_QUOTER_ABI, ethersProvider) : void 0,
          chainId: effectiveChainId
        };
        const [v2Result, v3Result] = await Promise.allSettled([
          skipV2 ? Promise.resolve(null) : getV2Quote(v2Params),
          skipV3 ? Promise.resolve(null) : getV3Quote(v3Params)
        ]);
        if (runId !== quoteRunIdRef.current) {
          return;
        }
        setV2Loaded(true);
        setV3Loaded(true);
        setV2Loading(false);
        setV3Loading(false);
        let v2Trade = null;
        let v3Quote = null;
        let v2Error = null;
        let v3Error = null;
        if (v2Result.status === "fulfilled") {
          v2Trade = v2Result.value;
        } else {
          v2Error = v2Result.reason;
          routingError("V2 quote error", v2Error);
        }
        if (v3Result.status === "fulfilled") {
          v3Quote = v3Result.value;
        } else {
          v3Error = v3Result.reason;
          routingError("V3 quote error", v3Error);
        }
        if (!v2Trade && !v3Quote) {
          setTrade(null);
          const noRoutesError = new Error("Insufficient liquidity for this trade");
          noRoutesError.message = "Insufficient liquidity for this trade";
          setError(noRoutesError);
          setIsLoading(false);
          return;
        }
        const comparison = compareTrades(v2Trade, v3Quote, tradeType, currencyIn, currencyOut);
        const gasEstimateUSD = "5.00";
        const wrappedTrade = createTradeObject(comparison, tradeType, currencyIn, currencyOut, gasEstimateUSD);
        if (wrappedTrade) {
          ;
          wrappedTrade.routing = "CLASSIC";
          const defaultSlippage = fotAutoSlippage ?? 0.5;
          wrappedTrade.slippageTolerance = customSlippageTolerance || autoSlippageTolerance || defaultSlippage;
        }
        setTrade(wrappedTrade);
      } catch (err) {
        routingError("Routing error", err);
        if (runId !== quoteRunIdRef.current) {
          return;
        }
        setError(err);
      } finally {
        if (runId === quoteRunIdRef.current) {
          setIsLoading(false);
          setQuoteUpdatedAt(Date.now());
        }
      }
    }
    findBestTrade();
  }, [
    shouldSkip,
    tokenIn,
    tokenOut,
    amountSpecified,
    tradeType,
    currencyIn?.symbol,
    currencyOut?.symbol,
    effectiveChainId,
    customSlippageTolerance,
    autoSlippageTolerance,
    poolData,
    ethersProvider,
    bscContracts?.quoterV2,
    refreshKey,
    skipV2,
    skipV3
  ]);
  return useMemo(
    () => ({
      trade,
      isLoading: isLoading || poolsLoading,
      error,
      refetch,
      quoteUpdatedAt
    }),
    [trade, isLoading, poolsLoading, error, refetch, quoteUpdatedAt]
  );
}
